import { useEffect, useMemo, useState } from 'react'
import { ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { SkillApi, SkillTreeNode } from '../../services/skillApi'
import SkillMarkdown from './SkillMarkdown'
import { SkillCopyGuard } from './SkillCopyGuard'

const TEXT_TYPES = new Set([
  'md', 'py', 'json', 'txt', 'yaml', 'yml', 'ts', 'js', 'tsx', 'jsx',
  'sh', 'bat', 'ps1', 'xml', 'html', 'htm', 'css', 'toml', 'ini', 'csv', 'env', 'example',
])

function unquote(value: string): string {
  const v = value.trim()
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    return v.slice(1, -1)
  }
  return v
}

/**
 * Strip YAML frontmatter so Markdown doesn't join name/description into one
 * paragraph. Handles the shapes used by skill files: inline values, block
 * scalars (`|`, `>`), indented continuations and list values.
 */
function splitMarkdownFrontmatter(raw: string): {
  meta: Record<string, string>
  body: string
} {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/)
  if (!match) return { meta: {}, body: raw }

  const meta: Record<string, string> = {}
  const lines = match[1].split(/\r?\n/)
  let currentKey: string | null = null
  let buffer: string[] = []

  const flush = () => {
    if (!currentKey) return
    const joined = buffer.join('\n').replace(/\n{3,}/g, '\n\n').trim()
    meta[currentKey] = joined
    currentKey = null
    buffer = []
  }

  for (const line of lines) {
    const keyMatch = line.match(/^([A-Za-z0-9_.-]+)\s*:\s*(.*)$/)
    if (keyMatch) {
      flush()
      currentKey = keyMatch[1].trim()
      const inline = keyMatch[2].trim()
      // Block scalar markers introduce the value on the following lines.
      if (inline && !/^[|>][-+]?$/.test(inline)) buffer.push(unquote(inline))
      continue
    }

    if (currentKey === null) continue
    const item = line.match(/^\s*-\s+(.*)$/)
    if (item) {
      buffer.push(`• ${unquote(item[1])}`)
    } else if (line.trim()) {
      buffer.push(unquote(line))
    }
  }
  flush()

  return { meta, body: raw.slice(match[0].length) }
}

interface SkillFileViewerProps {
  node: SkillTreeNode | null
  courseId?: number
}

const SkillFileViewer = ({ node, courseId }: SkillFileViewerProps) => {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fileType = (node?.fileType || '').toLowerCase()
  const isText = TEXT_TYPES.has(fileType)
  const isPdf = fileType === 'pdf'
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType)

  const mdParts = useMemo(() => {
    if (fileType !== 'md' || content === null) return null
    return splitMarkdownFrontmatter(content)
  }, [fileType, content])

  useEffect(() => {
    if (!node || !isText) {
      setContent(null)
      setError(null)
      return
    }

    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await SkillApi.getFileContent(node.id, { courseId })
        if (!cancelled) setContent(data.content)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Không đọc được file')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [node?.id, isText, courseId])

  if (!node) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed border-m2-outline-variant bg-m2-surface-low text-m2-on-surface-variant">
        <DocumentIcon className="mb-3 h-12 w-12 text-m2-outline" />
        <p>Chọn một file để xem nội dung</p>
      </div>
    )
  }

  const token = localStorage.getItem('accessToken') || ''
  const streamUrl = SkillApi.fileStreamUrl(node.id, { courseId, raw: true })

  const downloadBtn = (
    <a
      href={streamUrl}
      download={node.name}
      className="inline-flex items-center gap-1 text-sm text-m2-primary hover:underline"
      onClick={async (e) => {
        e.preventDefault()
        if (!token) {
          window.location.href = `/login?returnUrl=${encodeURIComponent(window.location.pathname)}`
          return
        }
        try {
          const res = await fetch(streamUrl, {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (!res.ok) throw new Error('Download failed')
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = node.name
          a.click()
          URL.revokeObjectURL(url)
        } catch {
          window.open(streamUrl, '_blank')
        }
      }}
    >
      <ArrowDownTrayIcon className="h-4 w-4" />
      Tải xuống
    </a>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
      <div className="flex items-center justify-between gap-3 border-b border-m2-outline-variant bg-m2-surface-low px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-m2-on-surface">{node.name}</h3>
          <p className="truncate text-xs text-m2-on-surface-variant">{node.relativePath}</p>
        </div>
        {downloadBtn}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading && <p className="text-sm text-m2-on-surface-variant">Đang tải nội dung...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!loading && !error && fileType === 'md' && mdParts && (
          <SkillCopyGuard>
            <div className="space-y-4">
              {Object.keys(mdParts.meta).length > 0 && (
                <div className="rounded-eh-sm border border-eh-border bg-eh-keylime/45 px-4 py-3 text-sm text-eh-charcoal">
                  {(['name', 'description', 'context'] as const).map((key) =>
                    mdParts.meta[key] ? (
                      <div key={key} className="py-1.5 first:pt-0 last:pb-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-eh-muted">
                          {key}
                        </div>
                        <div
                          className={
                            key === 'name'
                              ? 'mt-0.5 font-semibold text-eh-forest-ink'
                              : 'mt-0.5 whitespace-pre-wrap break-words text-eh-muted'
                          }
                        >
                          {mdParts.meta[key]}
                        </div>
                      </div>
                    ) : null
                  )}
                  {Object.entries(mdParts.meta)
                    .filter(([key]) => !['name', 'description', 'context'].includes(key))
                    .map(([key, value]) => (
                      <div key={key} className="py-1.5">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-eh-muted">
                          {key}
                        </div>
                        <div className="mt-0.5 whitespace-pre-wrap break-words text-eh-muted">
                          {value}
                        </div>
                      </div>
                    ))}
                </div>
              )}
              <SkillMarkdown body={mdParts.body} baseNodeId={node.id} courseId={courseId} />
            </div>
          </SkillCopyGuard>
        )}

        {!loading && !error && isText && fileType !== 'md' && content !== null && (
          <SkillCopyGuard>
            <pre className="overflow-x-auto rounded-eh-sm border border-eh-border bg-eh-cream p-4 text-sm leading-relaxed text-eh-charcoal">
              <code>{content}</code>
            </pre>
          </SkillCopyGuard>
        )}

        {isPdf && (
          <PdfViewer nodeId={node.id} title={node.name} courseId={courseId} />
        )}

        {isImage && <AuthImage nodeId={node.id} alt={node.name} courseId={courseId} />}

        {!isText && !isPdf && !isImage && (
          <div className="flex flex-col items-center justify-center py-16 text-m2-on-surface-variant">
            <DocumentIcon className="mb-3 h-12 w-12" />
            <p className="mb-4 text-sm">Loại file này không hỗ trợ xem trực tiếp</p>
            {downloadBtn}
          </div>
        )}
      </div>
    </div>
  )
}

const AuthImage = ({
  nodeId,
  alt,
  courseId,
}: {
  nodeId: number
  alt: string
  courseId?: number
}) => {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    const load = async () => {
      const headers: HeadersInit = {}
      const token = localStorage.getItem('accessToken')
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await fetch(SkillApi.fileStreamUrl(nodeId, { courseId }), { headers })
      if (!res.ok || cancelled) return
      const blob = await res.blob()
      objectUrl = URL.createObjectURL(blob)
      if (!cancelled) setUrl(objectUrl)
    }
    load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [nodeId, courseId])

  if (!url) return <p className="text-sm text-m2-on-surface-variant">Đang tải ảnh...</p>
  return <img src={url} alt={alt} className="max-h-[70vh] max-w-full object-contain" />
}

const PdfViewer = ({
  nodeId,
  title,
  courseId,
}: {
  nodeId: number
  title: string
  courseId?: number
}) => {
  const [url, setUrl] = useState<string | null>(null)
  const [needLogin, setNeedLogin] = useState(false)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    setNeedLogin(false)
    const load = async () => {
      const headers: HeadersInit = {}
      const token = localStorage.getItem('accessToken')
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await fetch(SkillApi.fileStreamUrl(nodeId, { courseId }), { headers })
      if (res.status === 401 && !cancelled) {
        setNeedLogin(true)
        return
      }
      if (!res.ok || cancelled) return
      const blob = await res.blob()
      objectUrl = URL.createObjectURL(blob)
      if (!cancelled) setUrl(objectUrl)
    }
    load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [nodeId, courseId])

  if (needLogin) {
    return (
      <p className="text-sm text-m2-on-surface-variant">
        <a
          className="text-m2-primary hover:underline"
          href={`/login?returnUrl=${encodeURIComponent(window.location.pathname)}`}
        >
          Đăng nhập
        </a>{' '}
        để xem tài liệu PDF.
      </p>
    )
  }

  if (!url) {
    return <p className="text-sm text-m2-on-surface-variant">Đang tải PDF...</p>
  }

  return (
    <iframe src={url} title={title} className="h-[70vh] w-full rounded border-0" />
  )
}

export default SkillFileViewer
